import React from 'react';
import { useSelector } from 'react-redux';

import { makeStyles } from 'tss-react/mui';
import { Container, Grid, units } from '@hbf/dsl';

import { useMediaQuery } from 'ha/helpers/MediaQuery';
import {
  ListingCancellationPolicy,
  ListingFacilities,
} from '@ha/api/v2/types/Listing';
import { LazyComponent } from 'ha/components.legacy/LazyComponent';

import { UserCardDefault } from 'ha/components/Redesign/UserCard/UserCardDefault';
import { ContactAdvertiserSection } from './components/ContactAdvertiserSection';

import { KamernetBanner } from './fragments/KamernetBanner';
import { ListingContactAdvertiserButton } from './fragments/ListingContactAdvertiserButton';
import { ListingHelmetContainer as ListingHelmet } from './fragments/ListingHelmet';
import { ListingInstantBookingBlock } from './fragments/ListingInstantBookingBlock';
import { ListingOverview } from './fragments/ListingOverview';

import { FullscreenMapContainer as FullscreenMap } from './containers/FullscreenMap';
import { ListingAdvertiserCardLite } from './containers/ListingAdvertiserCardLite';
import { ListingAdvertiserDescription } from './components/ListingAdvertiserDescription';
import { SimilarListings as ListingSimilarListings } from './containers/ListingSimilarListings';

import { usePageRedesign } from './hooks/usePageRedesign';
import { getAdvertiser, getAdvertiserInformation } from './selectors';

const useStyles = makeStyles()(theme => ({
  root: {
    flexGrow: 1,
  },
  spacer: {
    margin: theme.spacing(2),
  },
  sticky: {
    position: 'sticky',
    top: 0,
  },
  rentRadar: {
    marginTop: theme.spacing(-3),
    marginBottom: theme.spacing(5),
  },
  verticalSpacer: {
    marginTop: units.rem(2.9),
    paddingLeft: units.rem(1.563),
  },
}));

export interface Props {
  isInstantBookable: boolean;
  hasDescription: boolean;
  isBelongsToPartner: boolean;
  countryCode: string;
  kind: number;
  facilities: ListingFacilities;
  price: number;
  currency: string;
  lat: number;
  lng: number;
  cancellationPolicy: ListingCancellationPolicy;
  isCharitable?: boolean;
  isFreeListing?: boolean;
}

export const ListingPageLayout: React.FC<Props> = ({
  isInstantBookable,
  hasDescription,
  isBelongsToPartner,
  facilities,
  cancellationPolicy,
  countryCode,
  isCharitable = false,
  isFreeListing = false,
}) => {
  const { classes } = useStyles();
  const { lg, md } = useMediaQuery();

  React.useEffect(() => {
    // 👇️ scroll to top on page load
    window.scrollTo({ top: 0, left: 0 });
  }, []);

  const withPageRedesign = usePageRedesign();

  const advertiser = useSelector(getAdvertiser);
  const advertiserInformation = useSelector(getAdvertiserInformation);

  return (
    <React.Fragment>
      <KamernetBanner />
      <Container maxWidth="lg">
        <Grid
          container
          // eslint-disable-next-line no-nested-ternary
          spacing={lg ? (withPageRedesign ? 2 : 5) : 0}
          className={classes.root}
        >
          <Grid container item xs={12} />
          <Grid
            container
            item
            xs={12}
            data-test-locator="ListingPageLayout/Content"
          >
            <ListingHelmet />

            {withPageRedesign ? (
              <Grid item xs={12} md={8}>
                <ListingOverview
                  cancellationPolicy={cancellationPolicy}
                  countryCode={countryCode}
                  facilities={facilities}
                  hasDescription={hasDescription}
                  isBelongsToPartner={isBelongsToPartner}
                  isCharitable={isCharitable}
                  isFreeListing={isFreeListing}
                />
              </Grid>
            ) : (
              <ListingOverview
                cancellationPolicy={cancellationPolicy}
                countryCode={countryCode}
                facilities={facilities}
                hasDescription={hasDescription}
                isBelongsToPartner={isBelongsToPartner}
                isCharitable={isCharitable}
                isFreeListing={isFreeListing}
              />
            )}

            {withPageRedesign && md && (
              <Grid item md={4} className={classes.verticalSpacer}>
                <div className={classes.sticky}>
                  <ContactAdvertiserSection
                    hideNonFloatingSection={false}
                    isBelongsToPartner={isBelongsToPartner}
                    user={advertiser}
                  >
                    <UserCardDefault
                      linkTo="faq"
                      userInformation={advertiserInformation}
                    >
                      <ListingInstantBookingBlock
                        isBelongsToPartner={isBelongsToPartner}
                      />
                    </UserCardDefault>
                  </ContactAdvertiserSection>
                </div>
              </Grid>
            )}

            <div className={classes.spacer} />

            <Grid container item xs={12}>
              {!isBelongsToPartner && (
                <Grid
                  container
                  item
                  xs={12}
                  spacing={lg ? 5 : 3}
                  id="advertiser"
                  data-test-locator="Advertiser Details Bottom"
                >
                  <Grid item md={3} xs={12}>
                    <ListingAdvertiserCardLite>
                      {!isInstantBookable && lg && (
                        <ListingContactAdvertiserButton variant="advertiser-section" />
                      )}
                    </ListingAdvertiserCardLite>
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <ListingAdvertiserDescription />
                  </Grid>
                </Grid>
              )}
              {!isInstantBookable && (
                <LazyComponent
                  scrollableAncestor="window"
                  bottomOffset="-500px"
                >
                  <Grid item xs={12}>
                    <ListingSimilarListings />
                  </Grid>
                </LazyComponent>
              )}
            </Grid>

            {md ? null : <FullscreenMap />}
          </Grid>
          <Grid container item xs={12} />
        </Grid>
      </Container>
    </React.Fragment>
  );
};
